// src/admin/pages/KPI/KPIPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import KPIFilterBar       from "./components/kpi/KPIFilterBar";
import GMVSection         from "./components/kpi/GMVSection";
import PerformanceSection from "./components/kpi/PerformanceSection";
import ProcessoSection    from "./components/kpi/ProcessoSection";
import TrendModal         from "./components/kpi/TrendModal";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/* util */
const toLocalYYYYMMDD = d =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];

/* mappa cardKey → {metric,label} */
const METRIC_MAP = {
  /* GMV */
  delivered_orders_count: { metric: "delivered_orders",      label: "Ordini Consegnati" },
  total_revenue:          { metric: "total_revenue",         label: "Fatturato Totale (€)" },
  average_cart:           { metric: "average_cart",          label: "Carrello Medio (€)" },
  total_sessions_count:   { metric: "total_sessions_count",  label: "Sessioni" },
  total_ingredient_cost:  { metric: "total_ingredient_cost", label: "Costo Ingredienti (€)" },
  total_overhead_cost:    { metric: "total_overhead_cost",   label: "Costi Extra (€)" },

  /* Performance */
  margin_net:  { metric: "margin_net",  label: "Margine Netto (€)" },
  margin_pct:  { metric: "margin_pct",  label: "Margine %" },
  markup_pct:  { metric: "markup_pct",  label: "Markup %" },
  net_profit:  { metric: "net_profit",  label: "Profitto Netto (€)" },
  profit_pct:  { metric: "profit_pct",  label: "Profitto %" },

  /* Processo */
  avg_session_duration:    { metric: "avg_session_duration",    label: "Durata Sessione (s)" },
  avg_prep_time:           { metric: "avg_prep_time",           label: "Tempo Prep (s)" },
  avg_order_delivery_time: { metric: "avg_order_delivery_time", label: "Tempo Consegna (s)" },
  avg_queue_time:          { metric: "avg_queue_time",          label: "Tempo Coda (s)" },
  avg_total_time:          { metric: "avg_total_time",          label: "Tempo Totale (s)" },
};

export default function KPIPage() {
  /* date */
  const today = useMemo(() => toLocalYYYYMMDD(new Date()), []);
  const [startDate, setStartDate] = useState(today);
  const [endDate,   setEndDate]   = useState(today);

  /* ristoranti */
  const [restaurantsList,    setRestaurantsList]    = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState("all");
  const [trendRestaurant,    setTrendRestaurant]    = useState("all");

  /* area filter */
  const [areaFilter, setAreaFilter] = useState("all"); // all | kitchen | bar

  /* KPI */
  const [kpis,    setKpis]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [activeSection, setActiveSection] = useState("GMV");

  /* trend */
  const [trendCache, setTrendCache] = useState({});
  const [chartOpen,  setChartOpen]  = useState(false);
  const [chartMeta,  setChartMeta]  = useState({ metric:"", label:"" });

  /* fetch ristoranti */
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("access_token") || "";
        const res = await fetch(`${API_BASE_URL}/admin/kpi/restaurants_list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setRestaurantsList(await res.json());
      } catch { /* ignore */ }
    })();
  }, []);

  /* build params */
  const buildParams = (area) => {
    const p = new URLSearchParams();
    if (startDate)           p.append("start_date", startDate);
    if (endDate)             p.append("end_date",   endDate);
    if (selectedRestaurant !== "all")
                             p.append("restaurant_id", selectedRestaurant);
    if (area !== "all")      p.append("area", area);
    return p;
  };

  /* fetch KPI */
  const fetchKpis = async () => {
    try {
      setLoading(true); setError(null);
      const token = localStorage.getItem("access_token") || "";

      const r1 = await fetch(`${API_BASE_URL}/admin/kpi?${buildParams(areaFilter)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r1.ok) throw new Error();
      const k1 = await r1.json();

      if (areaFilter !== "all") {
        const r2 = await fetch(`${API_BASE_URL}/admin/kpi?${buildParams("all")}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r2.ok) {
          const k2 = await r2.json();
          k1.avg_session_duration = k2.avg_session_duration;
        }
      }
      setKpis(k1);
    } catch {
      setError("Impossibile caricare i KPI");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKpis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedRestaurant, areaFilter]);

  /* fetch serie trend */
  const fetchSeries = async (metric, rid) => {
    const areaParam = metric === "avg_session_duration" ? "all" : areaFilter;
    const key = `${metric}|${rid}|${areaParam}`;
    if (trendCache[key]) return trendCache[key];

    try {
      const token = localStorage.getItem("access_token") || "";
      const p = new URLSearchParams({ "metrics[]": metric });
      if (rid !== "all")       p.append("restaurant_id", rid);
      if (areaParam !== "all") p.append("area", areaParam);

      const res = await fetch(`${API_BASE_URL}/admin/kpi/trend?${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];

      const json  = await res.json();
      const serie = json.series?.[metric] || [];
      setTrendCache(prev => ({ ...prev, [key]: serie }));
      return serie;
    } catch {
      return [];
    }
  };

  /* refetch serie quando cambia filtro area / restaurant / metric con modale aperta */
  useEffect(() => {
    if (!chartOpen || !chartMeta.metric) return;
    fetchSeries(chartMeta.metric, trendRestaurant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaFilter, trendRestaurant, chartMeta.metric, chartOpen]);

  /* open trend */
  const openTrendWrapper = async (cardKey) => {
    const cfg = METRIC_MAP[cardKey];
    if (!cfg) return;
    setChartMeta({ metric: cfg.metric, label: cfg.label });
    setChartOpen(true);
    await fetchSeries(cfg.metric, trendRestaurant);
  };

  /* cambio ristorante dentro modale */
  const handleTrendRestaurantChange = async (newRid) => {
    setTrendRestaurant(newRid);
    if (chartMeta.metric) await fetchSeries(chartMeta.metric, newRid);
  };

  /* key cache helper */
  const cacheKey = `${chartMeta.metric}|${trendRestaurant}|${
    chartMeta.metric === "avg_session_duration" ? "all" : areaFilter
  }`;

  /* render */
  return (
    <div style={styles.pageContainer}>
      <KPIFilterBar
        startDate={startDate}
        endDate={endDate}
        onChangeStart={setStartDate}
        onChangeEnd={setEndDate}
        onFilter={fetchKpis}
        restaurantsList={restaurantsList}
        selectedRestaurant={selectedRestaurant}
        onChangeRestaurant={setSelectedRestaurant}
      />

      {/* tab bar */}
      <div style={styles.tabBar}>
        {["GMV","Performance","Processo"].map(sec => (
          <button
            key={sec}
            onClick={() => setActiveSection(sec)}
            style={{
              ...styles.tabButton,
              ...(activeSection===sec ? styles.tabButtonActive : {}),
            }}
          >
            {sec}
          </button>
        ))}
      </div>

      {loading && <p>Caricamento…</p>}
      {error   && <p style={{color:"red"}}>{error}</p>}

      {kpis && (
        <>
          {activeSection==="GMV" && (
            <GMVSection kpis={kpis} onCardClick={openTrendWrapper} />
          )}
          {activeSection==="Performance" && (
            <PerformanceSection kpis={kpis} onCardClick={openTrendWrapper} />
          )}
          {activeSection==="Processo" && (
            <ProcessoSection
              kpis={kpis}
              onCardClick={openTrendWrapper}
              area={areaFilter}
              onAreaChange={setAreaFilter}
            />
          )}
        </>
      )}

      {/* Trend modale */}
      <TrendModal
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        label={chartMeta.label}
        metricKey={chartMeta.metric}
        data={trendCache[cacheKey] || []}
        restaurantsList={restaurantsList}
        selectedRestaurant={trendRestaurant}
        onRestaurantChange={handleTrendRestaurantChange}
        area={areaFilter}
        onAreaChange={setAreaFilter}
      />
    </div>
  );
}

/* styles */
const styles = {
  pageContainer: {
    background: "#FAFAFA",
    minHeight: "100vh",
    padding: "1rem",
  },
  tabBar: {
    marginTop: "1rem",
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
  },
  tabButton: {
    flex: "1 1 auto",
    padding: "0.6rem 1rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
    background: "#fff",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  tabButtonActive: {
    background: "#336699",
    color: "#fff",
    borderColor: "#336699",
  },
};
